import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useEventListener, useIsMobile, useNavigate } from '@utils/hooks';

import { operationBlockActions, useStore } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { UniqueElementDataType } from '@modules/operationBlock/types';

import Button from '@components/Button';
import { DataKeysEnum } from '@containers/OperationBlock/types';

import QRIcon from '../icons/qr.svg';

import styles from './Buttons.scss';

const cn = bemCssModules(styles, 'Buttons');
const ti = getTestId('Buttons');

const checkIsMoved = () => {
  const distanceToBottom = document.body.offsetHeight - window.innerHeight - window.pageYOffset;
  return distanceToBottom <= 30;
};

export type TButtons = {
  currency: CryptoCurrencyEnum;
};

const Buttons: CustomFC<TButtons> = props => {
  const { className, currency } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ cards, wallet, transactions, user }, dispatch] = useStore();
  const [isMoved, setIsMoved] = useState(false);
  const isMobile = useIsMobile();

  const handleRedirect = () => navigate(CONST.nav.wallets);

  const disableMove = isMobile && !user.isZHDisclaimer;

  useEventListener('scroll', () => setIsMoved(checkIsMoved()), { disabled: disableMove });

  useEffect(() => {
    const resizeObserver = !disableMove && new ResizeObserver(() => setIsMoved(checkIsMoved()));
    if (resizeObserver) resizeObserver.observe(document.body);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const setLeft = (group: UniqueElementDataType['group'], value: UniqueElementDataType['value']) =>
    dispatch(operationBlockActions.setLeft({ group, value }));
  const setRight = (group: UniqueElementDataType['group'], value: UniqueElementDataType['value']) =>
    dispatch(operationBlockActions.setRight({ group, value }));

  const cardsValue = cards.list[0]?.id || 'new_card';
  const balance = wallet.data?.[currency]?.balance || '0';
  const hasTransactions = !!Object.keys(transactions.list).length;
  const hasSendButton = Number(balance) > 0 && hasTransactions;

  const handleDeposit = () => {
    setLeft(DataKeysEnum.blockchain_wallets, currency);
    setRight(DataKeysEnum.my_wallets, currency);
    handleRedirect();
  };

  const handleTopup = () => {
    setLeft(DataKeysEnum.cards, cardsValue);
    setRight(DataKeysEnum.my_wallets, currency);
    handleRedirect();
  };

  const handleSend = () => {
    setLeft(DataKeysEnum.my_wallets, currency);
    setRight(DataKeysEnum.cards, cardsValue);
    handleRedirect();
  };

  return (
    <div className={cn({ isMoved }, [className])}>
      <Button className={cn('qr-button')} onClick={handleDeposit} testId={ti('deposit')}>
        <QRIcon />
      </Button>
      <Button className={cn('button')} onClick={handleTopup} testId={ti('topup')}>
        {t('operations.topup')}
      </Button>
      {hasSendButton && (
        <Button className={cn('button')} onClick={handleSend} testId={ti('send')}>
          {t('operations.send')}
        </Button>
      )}
    </div>
  );
};
export default Buttons;
